import React from 'react';
import { Box, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, Alert, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RoleForm, { permKeys } from './roles/RoleForm';
import { fetchRoles, createRole, RoleDto } from '../api/rolesApi';

type Role = {
  id?: string;
  name: string;
  perms: Record<string, boolean>;
};

function fromDto(dto: RoleDto): Role {
  return {
    id: dto.id !== undefined ? String(dto.id) : undefined,
    name: dto.roleName,
    perms: {
      groups: dto.groups,
      hardware: dto.hardware,
      profiles: dto.profile,
      devices: dto.device,
      userroles: dto.userRoles,
    },
  };
}

function toDto(role: Role): RoleDto {
  return {
    roleName: role.name,
    groups: Boolean(role.perms.groups),
    hardware: Boolean(role.perms.hardware),
    profile: Boolean(role.perms.profiles),
    device: Boolean(role.perms.devices),
    userRoles: Boolean(role.perms.userroles),
  };
}

const RolesPage: React.FC = () => {
  const [roles, setRoles] = React.useState<Role[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<string>('');
  const [viewedRole, setViewedRole] = React.useState<Role | null>(null);
  const [newOpen, setNewOpen] = React.useState(false);
  const [newRole, setNewRole] = React.useState<Role>({ id: '', name: '', perms: {} });
  const [createError, setCreateError] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);

  const loadRoles = React.useCallback(() => {
    setLoading(true);
    setLoadError(null);
    fetchRoles()
      .then((dtos) => setRoles(dtos.map(fromDto)))
      .catch((err) => setLoadError(err.message || 'Failed to load roles.'))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const openNew = () => {
    setCreateError(null);
    setNewOpen(true);
  };
  const closeNew = () => setNewOpen(false);

  const handleCreate = async () => {
    if (!newRole.name) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await createRole(toDto(newRole));
      setRoles((r) => [fromDto(created), ...r]);
      setNewRole({ id: '', name: '', perms: {} });
      closeNew();
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create role.');
    } finally {
      setCreating(false);
    }
  };

  const handleSelectChange = (id: string) => {
    setSelected(id);
    setViewedRole(null);
  };

  const handleView = () => {
    const role = roles.find((r) => r.id === selected);
    setViewedRole(role || null);
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ color: '#e0e0e0' }}>View Role</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openNew} sx={{ textTransform: 'none' }}>New Role</Button>
      </Box>

      <Paper sx={{ bgcolor: '#141414', color: '#e0e0e0', borderRadius: 1, p: 3 }}>
        <Typography variant="h6" sx={{ color: '#e0e0e0', mb: 2 }}>Please select a Role name and view its Authorization</Typography>

        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={loadRoles}>Retry</Button>}>
            {loadError}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2, alignItems: 'center' }}>
          <Box sx={{ fontWeight: 700, color: '#e0e0e0' }}>Select a Role name:</Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <select value={selected} onChange={(e) => handleSelectChange(e.target.value)} style={{ width: 260, padding: '12px', borderRadius: 6 }} disabled={loading}>
              <option value="">-- select --</option>
              {roles.map((r) => (<option key={r.id} value={r.id}>{r.name}</option>))}
            </select>
            {loading && <CircularProgress size={20} />}
          </Box>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Button variant="contained" onClick={handleView} disabled={!selected} sx={{ px: 6, py: 1.5,backgroundColor: '#1976d2', fontWeight: 700 }}>View</Button>
        </Box>

        {viewedRole && (
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #333' }}>
            <Typography variant="subtitle1" sx={{ color: '#e0e0e0', mb: 2, fontWeight: 700 }}>
              Authorization for "{viewedRole.name}"
            </Typography>
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {permKeys.map((p) => (
                <Box key={p.key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ color: '#e0e0e0', width: 280 }}>{p.label}</Box>
                  <Box sx={{ color: viewedRole.perms[p.key] ? '#4caf50' : '#9e9e9e', fontWeight: 700 }}>
                    {viewedRole.perms[p.key] ? 'Yes' : 'No'}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      <Dialog open={newOpen} onClose={closeNew} fullWidth maxWidth="sm">
        <DialogTitle>New Role</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          <RoleForm value={newRole} onChange={(r) => setNewRole(r)} />
        </DialogContent>
        <Box sx={{ p: 2 }}>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !newRole.name} sx={{ backgroundColor: '#1976d2',px: 4, py: 1.5, fontWeight: 700 }}>
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default RolesPage;
